<template>
  <div class="map" ref="measure">
    <client-only>
      <mapbox-map
        v-if="initialCenter && initialZoom"
        :auto-resize="true"
        @loaded="loaded"
        access-token="pk.eyJ1IjoiZHN0YXVkaWdlbCIsImEiOiJjazNueTNoNm0xdjZ5M2ZudXY3ZWN4aW0xIn0.tCf460GP2JsHd1DxJ-nMNA"
        :map-style="mapStyle"
        :pitch="0"
        :max-pitch="0"
        :zoom="initialZoom"
        :center="initialCenter"
        @touchstart="dragging = true"
        @touchend="dragging = false"
        @click="click"
        @dblclick="$emit('dblclick', $event)"
      >
        <slot v-if="map" />
      </mapbox-map>
    </client-only>
  </div>
</template>
<script lang="ts" setup>
import Mapbox, { Map } from "mapbox-gl";
import { viewport } from "@mapbox/geo-viewport";

import displayCombined from "../lib/map-styles/display-combined";
import { useElementSize, useVModel } from "@vueuse/core";
import {
  provideMap,
  provideMapBounds,
  provideMapCenter,
  provideMapDragging,
  provideZoom,
} from "../composables/use-map";

const mapStyle = ref(displayCombined);

if (import.meta.hot) {
  import.meta.hot.accept(
    "../lib/map-styles/display-combined",
    (newDisplayCombined) => {
      // do this in a sub-component?
      // console.log("New display combined: ", newDisplayCombined);
      map.value?.setStyle(newDisplayCombined?.default);
    }
  );
}

const { LngLatBounds } = Mapbox;
const measure = ref();
const { width, height } = useElementSize(measure);

const props = defineProps<{
  class?: string;
  center?: { lat: number; lng: number };
  zoom?: number;
  fit?: { lat: number | string; lng: number | string }[];
  padTop?: boolean;
}>();

const emit = defineEmits([
  "update:bounds",
  "update:center",
  "update:zoom",
  "click",
  "dblclick",
]);

const zoom = useVModel(props, "zoom", emit);
const quickZoom = ref(zoom.value);
const initialZoom = ref();

const center = useVModel(props, "center", emit);

const initialCenter = ref({ ...center.value });
const quickCenter = ref<{ lat: number; lng: number }>(
  center.value ? { ...center.value } : undefined
);

const dragging = ref(false);

provideMapDragging(dragging);

if (!props.center && props.fit && props.fit.length > 0) {
  const doneFitting = watch([width, height], () => {
    if (!width.value || !height.value || !props.fit) return;

    const bounds = boundsFor(props.fit);

    const v = viewport(
      [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ],
      [width.value, height.value]
    );

    quickZoom.value = zoom.value = initialZoom.value = v.zoom - 1;
    quickCenter.value = { lng: v.center[0], lat: v.center[1] };
    center.value = initialCenter.value = { lng: v.center[0], lat: v.center[1] };

    doneFitting();
  });
} else {
  quickZoom.value = zoom.value = initialZoom.value = props.zoom || 5;
  center.value = initialCenter.value = props.center
    ? { lng: Number(props.center.lng), lat: Number(props.center.lat) }
    : { lng: -90, lat: 39 };
}

provideMapCenter(quickCenter);
provideZoom(quickZoom);

const map = ref<Mapbox.Map>();
const bounds = ref(null);
const paddingTop = computed(() => (props.padTop ? height.value / 2 : 0));

provideMapBounds(bounds);
provideMap(map);

watch(paddingTop, () => {
  if (map.value)
    map.value.setPadding({
      top: paddingTop.value,
      bottom: 0,
      left: 0,
      right: 0,
    });
});

function updateBounds() {
  const { lat, lng } = map.value.getCenter();

  quickZoom.value = zoom.value = map.value.getZoom();

  quickCenter.value = { lat, lng };
  center.value = { lat, lng };
  const mapBounds = map.value.getBounds();
  bounds.value = {
    south: mapBounds.getSouth(),
    north: mapBounds.getNorth(),
    east: mapBounds.getEast(),
    west: mapBounds.getWest(),
  };

  emit("update:bounds", bounds.value);
}

function loaded(mapboxMap: Map) {
  map.value = mapboxMap;
  mapboxMap.setPadding({ top: paddingTop.value, bottom: 0, left: 0, right: 0 });

  mapboxMap.on("zoom", (e) => {
    quickZoom.value = map.value!.getZoom();
  });
  mapboxMap.on("move", (e) => {
    quickCenter.value = map.value!.getCenter();
  });

  mapboxMap.on("moveend", updateBounds);
  mapboxMap.on("zoomend", updateBounds);
}

function click($event) {
  if ($event instanceof MouseEvent) return;
  emit("click", $event);
}

defineExpose({
  setLayoutProperty(
    ...args: Parameters<Mapbox.Map["setLayoutProperty"]>
  ): ReturnType<Mapbox.Map["setLayoutProperty"]> {
    return map.value.setLayoutProperty(...args);
  },
  setPaintProperty(
    ...args: Parameters<Mapbox.Map["setPaintProperty"]>
  ): ReturnType<Mapbox.Map["setPaintProperty"]> {
    return map.value.setPaintProperty(...args);
  },
  fit(checkpoints: Checkpoint[]) {
    const bounds = boundsFor(checkpoints);

    const v = viewport(
      [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ],
      [width.value, height.value]
    );

    map.value?.flyTo({
      center: v.center,
      zoom: v.zoom - 1,
    });

    //quickZoom.value = zoom.value = initialZoom.value = v.zoom - 1;
    //center.value = initialCenter.value = { lng: v.center[0], lat: v.center[1] };
  },
});
</script>
<style scoped>
.map {
  width: 100%;
  height: 100%;
}
</style>
